import React, { useEffect, useState } from "react";

import {
    Linking,
    Modal,
    Platform,
    Text,
    View
} from "react-native";
import Button from "../../../designSystem/Button";
import PremiumButton from "../../../designSystem/PremiumButton";
import Activity from "../../../models/Activity";
import { getLanguage } from "../../../utils/Localization/Localization";
import AppTheme from "../../../utils/Theme";
import { trackEvent, analyticsEventActivityLaunch, propertiesEventContent, propertiesEventAccess, propertiesEventCategory, propertiesEventName, analyticsEventActivityCompleted } from "../../../services/Tracking/Analytics";
import User from "../../../data/user/User";
import TutorialPager from "../../Tutorials/TutorialPager";
import LiliWebView from "../../../../specific/components/Webview/LiliWebView";
import LiliVideoPlayer from "../../../../specific/components/VideoPlayer/LiliVideoPlayer";
import { getUrlToUse } from "../../../utils/LocalStorage";
import { getAppTarget } from "../../../../specific/utils/Navigation/Host";
import { getVideoUrlForMediaId } from "../../../../specific/services/AppWrite/AppWriteDatabase";

import RoundIconButton from "../../../designSystem/RoundIconButton";
import Game from "../../Game/Game";
import { IcDraw, IcOrigami, IcTheater } from "../../../utils/Icons";

type localizedString = {
    fr?: string
}

interface ActivityCellActionGameProps {
    activity: Activity,
    isEnable?: boolean,
    componentId?: string,
    title: localizedString,
    color: string,
    width: number,
    categoryAnalyticsKey: string,
    sectionAnalyticsKey: string,
    context: string,
    sub_type: string,
    rules?: string[],
    starting_point?: "superpower" | "section"
}

const ActivityCellActionGame = (props: ActivityCellActionGameProps) => {
    const { isEnable = false, color, width, componentId = "", title, activity, context, categoryAnalyticsKey, sectionAnalyticsKey, sub_type, rules, starting_point = "superpower" } = props;

    const appTheme: AppTheme = new AppTheme();
    const user: User = User.getInstance();

    const [launchGame, setLaunchGame] = useState<boolean>(false);
    const [videoUrl, setVideoUrl] = useState<string|undefined>(undefined);
    const [viewDidAppear, setViewDidAppear] = useState<boolean>(false);

    useEffect(() => {
        if (videoUrl !== undefined) {
            setViewDidAppear(true);
        }
    }, [videoUrl]);

    const onViewWillAppear = async () => {
        if (sub_type === "video") {
            let videoUrlToUse = getVideoUrlForMediaId({ media_id: activity.key });
            videoUrlToUse = await getUrlToUse({ url: videoUrlToUse, activityKey: activity.key });
            setVideoUrl(videoUrlToUse);
        } else {
            setVideoUrl("");
        }
    }

    const launchTheGame = () => {
        trackEvent(analyticsEventActivityLaunch, {
            [propertiesEventCategory]: categoryAnalyticsKey,
            [propertiesEventContent]: sub_type,
            [propertiesEventAccess]: activity.isPremium() === true ? "premium" : "free",
            [propertiesEventName]: activity.analytics_key,
            "starting_point": starting_point
        });
        if ((sub_type === "web") && (Platform.OS === "web")) {
            const appTarget = getAppTarget();
            if ((appTarget === "gar-prod") || (appTarget === "gar-preprod")) {
                const contactUrlForGar = "https://sortie-confiance.gar.education.fr?idRessource=ark%3A%2F19153%2Frcqbq94w21%2Fs3&IDO=" + user.tne_ido + "&urlDest=" + encodeURI(activity.action.url);
                Linking.openURL(contactUrlForGar);
            } else {
                Linking.openURL(activity.action.url);
            }
        } else {
            setLaunchGame(true);
        }
    }

    const onGameCompleted = () => {
        trackEvent(analyticsEventActivityCompleted, {
            [propertiesEventCategory]: categoryAnalyticsKey,
            [propertiesEventContent]: sub_type,
            [propertiesEventAccess]: activity.isPremium() === true ? "premium" : "free",
            [propertiesEventName]: activity.analytics_key,
            "starting_point": starting_point
        });
    }

    const closeTheGame = () => {
        setLaunchGame(false);
    }

    const getGameContent = () => {

        if ((sub_type === "game") || (sub_type === "game_img")) {
            return <Game
                onGameCompleted={onGameCompleted}
                closeTheGame={closeTheGame}
                activity={activity}
                color={color} />;
        } else if (sub_type === "tuto") {
            return <TutorialPager
                onTutorialCompleted={onGameCompleted}
                rules={rules}
                closeTheTutorial={closeTheGame}
                didAppear={launchGame}
                activity={activity}
                color={color} />
        } else if (sub_type === "web") {
            return <LiliWebView color={color} closeTheDocument={closeTheGame} title="" url={activity.action.url} />
        } else if (sub_type === "video") {
            return <LiliVideoPlayer
                onVideoCompleted={onGameCompleted}
                url={videoUrl}
                closeTheDocument={closeTheGame} />
        }
    }

    let actionButton = <PremiumButton componentId={componentId} />;
    if (isEnable === true) {
        actionButton = <Button onPress={launchTheGame} title={title[getLanguage()]} style={{ minWidth: appTheme.pixelPerfect(140), backgroundColor: color, marginHorizontal: appTheme.pixelPerfect(20), marginBottom: appTheme.pixelPerfect(10) }} />;
        if ((sub_type === "game") || (sub_type === "game_img")) {
            actionButton = <RoundIconButton onPress={launchTheGame} iconName={IcTheater} backgroundColor={color} />;
        } else if (sub_type === "tuto") {
            if (categoryAnalyticsKey === "DRAWS") {
                actionButton = <RoundIconButton onPress={launchTheGame} iconName={IcDraw} backgroundColor={color} />;
            } else if (categoryAnalyticsKey === "ORIGA") {
                actionButton = <RoundIconButton onPress={launchTheGame} iconName={IcOrigami} backgroundColor={color} />;
            }
        }
    }

    const getContent = () => {
        if (viewDidAppear === false) {
            return <View onLayout={onViewWillAppear} style={{ width, height: width, alignItems: "center", marginTop: appTheme.pixelPerfect(10) }} />
        }
        return <View style={{ width, alignItems: "center", marginTop: appTheme.pixelPerfect(10) }}>
            {actionButton}
            <Modal animationType="slide" visible={launchGame} transparent={true} style={{ width: appTheme.getFullScreenWidth(), height: appTheme.getFullAppHeight() }}>
                <View style={{ flex: 1, alignItems: "center", backgroundColor: appTheme.daySkyColor, width: appTheme.getFullScreenWidth(), height: appTheme.getFullAppHeight() }}>
                    {getGameContent()}
                </View>
            </Modal>
        </View>
    }

    return getContent();
}

export default ActivityCellActionGame;